import { Component, OnInit, ViewChild } from '@angular/core';
import { API, DiurnalBucketDTO, TargetingDTO } from '../../../services/api/api';
import { SbDateTime } from '../../../services/api/api-date-time';
import { DateTime } from 'luxon';
import { ErrorService } from '../../../services/error.service';
import { ModalNavigationComponent } from '../../../services/gui/modal/modal-navigation-component';
import { ModalNavigationService } from '../../../services/gui/modal/modal-navigation.service';
import { ChartComponent, SbLineChartModel } from '@ngx-seatback/charts';
import { UtilService } from '../../../services/util.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'sb-page-targeting-overview',
  templateUrl: './targeting-overview.component.html',
  styleUrls: ['./targeting-overview.component.scss'],
  standalone: false,
})
export class TargetingOverviewComponent implements OnInit, ModalNavigationComponent {
  loading: boolean;
  from: SbDateTime;
  till: SbDateTime;
  debounceTimer: ReturnType<typeof setTimeout> = null;
  isInModalContext: boolean;

  targeting: TargetingDTO;
  buckets: DiurnalBucketDTO[];
  chartData: SbLineChartModel;

  @ViewChild('chart') chart: ChartComponent;

  public get chartDataValid(): boolean {
    return this.chart?.chartData?.data?.length > 0;
  }

  constructor(
    private api: API,
    private util: UtilService,
    private errorHandler: ErrorService,
    private translate: TranslateService,
    private modalNavigation: ModalNavigationService
  ) {
    this.buckets = [];
    this.targeting = null;
    this.from = new SbDateTime(DateTime.local().startOf('week'));
    this.till = new SbDateTime(DateTime.local().endOf('week'));
    this.loading = true;

    this.chartData = {
      height: 500,
      title: 'Targeting',
      categoryType: 'date',
      tensionX: 1,
      fill: true,
      width: 100,
      label: 'true',
      data: [],
      legend: true,
      autoZoom: true,
      showYAxisLabels: true,
      valueAssignments: [
        {
          value: 'target',
          color: '#8D6EAE',
          valueName: this.translate.instant('lang.targeting.amount'),
          categories: ['date'],
        },
        {
          value: 'reached',
          color: '#4393F1',
          valueName: this.translate.instant('lang.targeting.target.Playouts'),
          categories: ['date'],
        },
        {
          value: 'value',
          color: '#FF0B18',
          valueName: this.translate.instant('lang.targeting.percentageValue'),
          categories: ['date'],
          ownAxis: true,
          opposite: true,
          unit: '%',
          fill: false,
          autoColor: true,
          ranges: [
            {
              color: '#FFBF00',
              from: 90,
              to: 95,
            },
            {
              color: '#00CA48',
              from: 95,
              to: 105,
            },
            {
              color: '#FFBF00',
              from: 105,
              to: 110,
            },
          ],
        },
      ],
    };
  }

  ngOnInit(): void {
    if (this.isInModalContext) {
      // wait for modal animation
      setTimeout(() => {
        this.targeting = this.modalNavigation.getCurrentNavigation().data.targeting;
        this.refreshFromServer();
      }, 200);
    } else {
      this.refreshFromServer();
    }
  }

  private refreshFromServer() {
    if (this.from && this.till) {
      this.loading = true;
      this.api
        .ds_cm_Targeting_retrieveDiurnalBuckets(this.from, this.till, { targeting: this.targeting ? [this.targeting.id] : null })
        .subscribe({
          next: (buckets: DiurnalBucketDTO[]) => {
            this.buckets = buckets;
            if (this.buckets && this.buckets.length > 0) {
              this.updateChartData();
            } else {
              this.loading = false;
            }
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  requestDebounced() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.refreshFromServer();
    }, 500);
  }

  private updateChartData(): void {
    this.loading = true;
    const dataPoints: { date: Date; value: number; target: number; reached: number }[] = [];
    for (const bucket of this.buckets) {
      const dataPoint = {
        date: this.util.localDateToJSDate(bucket.date),
        target: bucket.target,
        reached: bucket.reached,
        value: Math.round((bucket.reached / bucket.target) * 100),
      };

      if (dataPoint.value < 0) {
        dataPoint.value = 0;
      }

      dataPoints.push(dataPoint);
    }

    this.chartData.data = dataPoints;
    this.chart.rerenderChart(this.chartData);

    this.loading = false;
  }

  public handleDateChange(dates: { fromDate: SbDateTime; toDate: SbDateTime }) {
    if (!dates?.toDate || !dates?.fromDate) {
      return;
    }

    dates.toDate.value = dates.toDate.value.set({ hour: 23, minute: 59 });

    if (this.from.toString() !== dates.fromDate.toString() || this.till.toString() !== dates.toDate.toString()) {
      this.from = dates.fromDate;
      this.till = dates.toDate;
      this.requestDebounced();
    }
  }
}
